/**
 * @fileoverview Shopify OAuth API service.
 * @module features/settings/api/shopifyOAuthService
 */

import { z } from 'zod'
import apiClient from '@/lib/apiClient'

/**
 * Schema for OAuth configuration response.
 */
export const ShopifyOAuthConfigSchema = z.object({
  clientId: z.string(),
  redirectUri: z.string(),
  scopes: z.string(),
  configured: z.boolean(),
})

export type ShopifyOAuthConfig = z.infer<typeof ShopifyOAuthConfigSchema>

/**
 * Schema for authorization URL response.
 */
export const AuthorizeResponseSchema = z.object({
  authorizationUrl: z.string(),
  state: z.string(),
})

export type AuthorizeResponse = z.infer<typeof AuthorizeResponseSchema>

/**
 * Schema for a single Shopify connection.
 */
export const ShopifyConnectionSchema = z.object({
  id: z.string().uuid(),
  shopDomain: z.string(),
  shopName: z.string().nullable(),
  webPixelId: z.string().nullable(),
  status: z.enum(['ACTIVE', 'ERROR', 'DISCONNECTED']),
  createdAt: z.string(),
})

export type ShopifyConnection = z.infer<typeof ShopifyConnectionSchema>

/**
 * Schema for connections list response.
 */
export const ConnectionsResponseSchema = z.object({
  connections: z.array(ShopifyConnectionSchema),
})

export type ConnectionsResponse = z.infer<typeof ConnectionsResponseSchema>

/**
 * Fetches Shopify OAuth configuration.
 *
 * @returns OAuth configuration
 */
export async function getShopifyOAuthConfig(): Promise<ShopifyOAuthConfig> {
  const res = await apiClient.get('/oauth/shopify/config')
  return ShopifyOAuthConfigSchema.parse(res.data)
}

/**
 * Connection mode for the Shopify store.
 */
export type ShopifyConnectionMode = 'DIRECT' | 'ATTRIBUTION_ONLY'

/**
 * Options for initiating Shopify OAuth authorization.
 */
export interface GetAuthorizationUrlOptions {
  /**
   * The shop domain (e.g., "mystore" or "mystore.myshopify.com")
   */
  shopDomain: string
  /**
   * Force connection even if OMS sources exist.
   * If true, bypasses ingestion mode validation.
   * Note: This parameter is only accepted in the JSON request body, not as a query parameter.
   */
  force?: boolean
  /**
   * Connection mode for the store.
   * - DIRECT: Orders synced directly from Shopify API
   * - ATTRIBUTION_ONLY: Only pixel tracking, orders come via OMS
   * Defaults to ATTRIBUTION_ONLY if not specified.
   */
  connectionMode?: ShopifyConnectionMode
}

/**
 * Gets the authorization URL to initiate OAuth flow for a specific shop.
 *
 * @param options Authorization options including shopDomain and optional force flag
 * @returns Authorization URL and state token
 * @throws 409 Conflict if OMS sources exist and force is not true
 */
export async function getShopifyAuthorizationUrl(
  options: GetAuthorizationUrlOptions | string
): Promise<AuthorizeResponse> {
  // Support both string (legacy) and options object
  const payload =
    typeof options === 'string'
      ? { shopDomain: options }
      : {
          shopDomain: options.shopDomain,
          force: options.force,
          connectionMode: options.connectionMode,
        }
  const res = await apiClient.post('/oauth/shopify/authorize', payload)
  return AuthorizeResponseSchema.parse(res.data)
}

/**
 * Gets list of connected Shopify stores.
 *
 * @returns List of Shopify connections
 */
export async function getShopifyConnections(): Promise<ShopifyConnection[]> {
  const res = await apiClient.get('/oauth/shopify/connections')
  const parsed = ConnectionsResponseSchema.parse(res.data)
  return parsed.connections
}

/**
 * Disconnects a Shopify store (works for both OAuth and Custom App connections).
 * Both connection types use the same underlying ShopifyConnection entity and
 * disconnect logic (unlink store, delete vault secret, delete connection).
 *
 * @param connectionId The connection ID to disconnect
 */
export async function disconnectShopify(connectionId: string): Promise<void> {
  await apiClient.delete(`/oauth/shopify/connections/${connectionId}`)
}

/**
 * Normalizes a shop domain input.
 * Handles various input formats:
 * - "mystore" -> "mystore.myshopify.com"
 * - "mystore.myshopify.com" -> "mystore.myshopify.com"
 * - "https://mystore.myshopify.com" -> "mystore.myshopify.com"
 *
 * @param input User input for shop domain
 * @returns Normalized shop domain
 */
export function normalizeShopDomain(input: string): string {
  let domain = input.trim().toLowerCase()

  // Remove protocol if present
  domain = domain.replace(/^https?:\/\//, '')

  // Remove trailing slash
  domain = domain.replace(/\/$/, '')

  // Add .myshopify.com if not present
  if (!domain.endsWith('.myshopify.com')) {
    domain = `${domain}.myshopify.com`
  }

  return domain
}

// ============================================================================
// Custom App Connection (Token-based, no OAuth)
// ============================================================================

/**
 * Schema for Custom App connection request (Dev Dashboard credentials).
 */
export const CustomAppConnectRequestSchema = z.object({
  shopDomain: z.string().min(1),
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  connectionMode: z.enum(['DIRECT', 'ATTRIBUTION_ONLY']).optional(),
  force: z.boolean().optional(),
})

export type CustomAppConnectRequest = z.infer<typeof CustomAppConnectRequestSchema>

/**
 * Schema for Custom App connection response.
 */
export const CustomAppConnectResponseSchema = z.object({
  connectionId: z.string().uuid(),
  shopDomain: z.string(),
  shopName: z.string().nullable(),
  connectionMode: z.string(),
  storeId: z.number(),
})

export type CustomAppConnectResponse = z.infer<typeof CustomAppConnectResponseSchema>

/**
 * Connects a Shopify store using Dev Dashboard app credentials.
 * Uses Client Credentials Grant to exchange clientId/clientSecret for access token.
 *
 * @param request Connection request with shop domain, clientId, and clientSecret
 * @returns Connection response with store details
 * @throws 400 if credentials are invalid or app not installed on store (code: INVALID_TOKEN)
 * @throws 403 if token is missing required scopes (code: INSUFFICIENT_SCOPES)
 * @throws 409 if OMS sources exist and force is not true
 * @see https://shopify.dev/docs/apps/build/authentication-authorization/access-tokens/client-credentials-grant
 */
export async function connectShopifyCustomApp(
  request: CustomAppConnectRequest
): Promise<CustomAppConnectResponse> {
  const res = await apiClient.post('/shopify/custom-app/connect', request)
  return CustomAppConnectResponseSchema.parse(res.data)
}

